/**
 * AdminLayout Component
 *
 * Layout wrapper for admin pages with sidebar navigation.
 * Provides consistent structure for all admin dashboard pages.
 */

import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Container } from '@/components/ui/Container'
import { Card } from '@/components/ui/Card'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { useAdminBreadcrumbs } from '@/hooks/useBreadcrumbs'

interface AdminLayoutProps {
  children: ReactNode
}

interface AdminNavItem {
  path: string
  label: string
  icon: string
}

const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { path: '/admin', label: 'Dashboard', icon: '📊' },
  { path: '/admin/theme-controls', label: 'Theme Controls', icon: '🎨' },
  { path: '/admin/content', label: 'Portfolio Content', icon: '✍️' },
  { path: '/admin/assets', label: 'Asset Management', icon: '🖼️' },
  { path: '/admin/projects', label: 'Projects', icon: '📁' },
  { path: '/admin/organizations', label: 'Organizations', icon: '🏢' },
  { path: '/admin/roles', label: 'Roles', icon: '💼' },
  { path: '/admin/evaluations', label: 'Evaluations', icon: '⭐' },
  { path: '/admin/cv', label: 'Secure CV', icon: '📄' },
  { path: '/admin/bio-builder', label: 'Bio Builder', icon: '🧬' },
]

export function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation()

  // UX12: Auto-generate breadcrumbs for admin pages
  const breadcrumbs = useAdminBreadcrumbs({
    '/admin': 'Dashboard',
    '/admin/theme-controls': 'Theme Controls',
    '/admin/content': 'Portfolio Content',
    '/admin/assets': 'Asset Management',
    '/admin/projects': 'Projects',
    '/admin/organizations': 'Organizations',
    '/admin/roles': 'Roles',
    '/admin/evaluations': 'Evaluations',
    '/admin/cv': 'Secure CV',
    '/admin/bio-builder': 'Bio Builder',
  })

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <Container size="xl">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '250px 1fr',
          gap: 'var(--spacing-2xl)',
          marginTop: 'var(--spacing-2xl)',
        }}
      >
        {/* Sidebar Navigation */}
        <aside>
          <Card padding="md">
            <h2
              style={{
                fontSize: '1.125rem',
                fontWeight: 600,
                marginBottom: 'var(--spacing-lg)',
                color: 'var(--color-text-primary)',
              }}
            >
              Admin Dashboard
            </h2>
            <nav>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {ADMIN_NAV_ITEMS.map((item) => (
                  <li key={item.path} style={{ marginBottom: 'var(--spacing-xs)' }}>
                    <Link
                      to={item.path}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)',
                        padding: 'var(--spacing-sm) var(--spacing-md)',
                        borderRadius: 'var(--radius-md)',
                        textDecoration: 'none',
                        color: isActive(item.path)
                          ? 'var(--color-primary)'
                          : 'var(--color-text-secondary)',
                        backgroundColor: isActive(item.path)
                          ? 'rgba(0, 0, 0, 0.05)'
                          : 'transparent',
                        fontWeight: isActive(item.path) ? 600 : 400,
                        transition: 'all var(--transition-fast)',
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive(item.path)) {
                          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.03)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive(item.path)) {
                          e.currentTarget.style.backgroundColor = 'transparent'
                        }
                      }}
                    >
                      <span style={{ fontSize: '1.25rem' }}>{item.icon}</span>
                      <span style={{ fontSize: '0.875rem' }}>{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </Card>
        </aside>

        {/* Main Content Area */}
        <main>
          {/* UX12: Breadcrumb navigation for context */}
          <Breadcrumb items={breadcrumbs} />
          {children}
        </main>
      </div>
    </Container>
  )
}
